import { selectContentClickHandler } from '@mnd-apps/public/lib/tracker';
import type { FeatureCardGridFragment } from '@mnd-apps/public/wp-generated/types';
import CardFeature from '@mnd-frontend/ui/public/components/CardFeature';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import SectionHeader from '@mnd-frontend/ui/public/components/wrappers/SectionHeader';
import { mapIconComponent } from '@mnd-frontend/ui/public/utils/maps/icon';
import {
  BREAKPOINTS,
  SPACING_2,
  SPACING_3,
  SPACING_6,
  SPACING_10,
  WEB_GREY_LT,
} from '@mnd-frontend/ui/shared/tokens';
import { transformStringToSnakeCase } from '@mnd-frontend/utils';
import dynamic from 'next/dynamic';
import { useId } from 'react';
import styled from 'styled-components';

const IconSparkle = dynamic(() => import('@mnd-frontend/icons/components/Sparkle'));
const IconAnalyze = dynamic(() => import('@mnd-frontend/icons/components/Analyze'));
const IconApi = dynamic(() => import('@mnd-frontend/icons/components/Api'));
const IconAward = dynamic(() => import('@mnd-frontend/icons/components/Award'));
const IconBook = dynamic(() => import('@mnd-frontend/icons/components/Book'));
const IconBriefcase = dynamic(() => import('@mnd-frontend/icons/components/Briefcase'));
const IconCreditCard = dynamic(() => import('@mnd-frontend/icons/components/CreditCard'));
const IconGraph = dynamic(() => import('@mnd-frontend/icons/components/Graph'));
const IconHeadphones = dynamic(() => import('@mnd-frontend/icons/components/Headphones'));
const IconHeart = dynamic(() => import('@mnd-frontend/icons/components/Heart'));
const IconInfo = dynamic(() => import('@mnd-frontend/icons/components/Info'));
const IconList = dynamic(() => import('@mnd-frontend/icons/components/List'));
const IconMegaphone = dynamic(() => import('@mnd-frontend/icons/components/Megaphone'));
const IconNotification = dynamic(() => import('@mnd-frontend/icons/components/Notification'));
const IconPaperPlane = dynamic(() => import('@mnd-frontend/icons/components/PaperPlane'));
const IconPodcast = dynamic(() => import('@mnd-frontend/icons/components/Podcast'));
const IconSearch = dynamic(() => import('@mnd-frontend/icons/components/Search'));
const IconSearchGraph = dynamic(() => import('@mnd-frontend/icons/components/SearchGraph'));
const IconSearchStar = dynamic(() => import('@mnd-frontend/icons/components/SearchStar'));
const IconSupport = dynamic(() => import('@mnd-frontend/icons/components/Support'));
const IconTextFile = dynamic(() => import('@mnd-frontend/icons/components/TextFile'));
const IconUndo = dynamic(() => import('@mnd-frontend/icons/components/Undo'));

const iconMap = {
  Ai: IconSparkle,
  Analyze: IconAnalyze,
  AnalyzeReport: IconGraph,
  Award: IconAward,
  Api: IconApi,
  Briefcase: IconBriefcase,
  Broadcast: IconPodcast,
  Finance: IconCreditCard,
  Follow: IconSearchStar,
  Heart: IconHeart,
  Info: IconInfo,
  Listening: IconHeadphones,
  Megaphone: IconMegaphone,
  Notification: IconNotification,
  Paper: IconTextFile,
  Plane: IconPaperPlane,
  PreciseResult: IconSearchGraph,
  Return: IconUndo,
  Search: IconSearch,
  Story: IconBook,
  Support: IconSupport,
  TailorList: IconList,
} as const;

const Section = styled.section`
  background: ${WEB_GREY_LT};
  padding: ${SPACING_6} ${SPACING_2};
  
  @media (min-width: ${BREAKPOINTS.XXL}px) { 
    padding: ${SPACING_10} 0;
  }
`;

const Grid = styled(ContentMaxWidth)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: ${SPACING_3};
  margin-top: 0;
  margin-bottom: 0;
  padding-left: 0;
`;

const FeatureCardModule = ({ data }: { data: FeatureCardGridFragment }) => {
  const cardId = useId();

  return (
    <Section>
      {data?.title && <SectionHeader headline={data.title} />}
      <Grid as="ul">
        {data?.cards?.map((card, index) => (
          <CardFeature
            key={index}
            id={`feature-${cardId}${index}`}
            title={card?.title || ''}
            titleAs={data?.title ? 'h3' : 'h2'}
            icon={mapIconComponent(card?.icon?.[0] ?? 'Plane', iconMap)}
            description={card?.description || ''}
            action={
              card?.link
                ? {
                    text: card.link?.title || '',
                    href: card.link?.url || '',
                    newTab: card.link?.target === '_blank',
                    onClick: selectContentClickHandler(
                      transformStringToSnakeCase(card?.title || 'feature-card'),
                      'feature-link',
                    ),
                  }
                : undefined
            }
            email={{
              label: card?.email || '',
              onClick: selectContentClickHandler(
                transformStringToSnakeCase(card?.title || 'feature-card'),
                'feature-email',
              ),
            }}
          />
        ))}
      </Grid>
    </Section>
  );
};

export default FeatureCardModule;
